import { useUser } from "apps/web/src/utils/tokens";
import { ApolloCache, NormalizedCacheObject } from "@apollo/client";
import React from "react";
import { trackEvent } from "apps/web/src/reporting/reporting";
import { useEditRecipeFullMutation, useEditRecipePartialMutation, useRecipeEditDialogQuery } from "../../../types";
import LoadingBackdrop from "../../universal/LoadingBackdrop";
import { RecipeFormState } from "../Form/utils";
import { RecipeEditContent } from "./RecipeEditContent";

// The server updates the allergens of every recipe that uses this one, so every cached recipe may be out of date.
// Evicting the fields makes any query or list that reads them ask the server again.
const evictCachedRecipeAllergens = (cache: ApolloCache<NormalizedCacheObject>) => {
  Object.keys(cache.extract())
    .filter(id => id.startsWith("Recipe:"))
    .forEach(id => {
      cache.evict({ id, fieldName: "allergens" });
      cache.evict({ id, fieldName: "selfAllergens" });
    });
  cache.gc();
};

interface RecipeEditProps {
  onClose: () => void;
  onEdit: () => void;
  recipeId: string;
  onDuplicate: () => void;
  forStaff?: boolean;
  readonly?: boolean;
  onScaleAndCreateNewRecipe: (state: RecipeFormState) => void;
}

export const RecipeEdit = ({ onClose, onEdit, recipeId, onDuplicate, forStaff, readonly, onScaleAndCreateNewRecipe }: RecipeEditProps) => {
  const user = useUser();
  const { data, loading } = useRecipeEditDialogQuery({
    variables: {
      id: recipeId,
    },
  });

  const isOrgGroupReadonly = Boolean(data?.recipe?.orgGroup) && Boolean(user) && user?.id !== data?.recipe.owner.id;

  const [editRecipeFull, { loading: savingFull }] = useEditRecipeFullMutation({
    onCompleted: () => {
      trackEvent("nutrition_recipe_edited", {});
      onEdit();
    },
  });

  const [editRecipePartial, { loading: savingPartial }] = useEditRecipePartialMutation({
    onCompleted: () => {
      trackEvent("nutrition_recipe_edited", {});
      onEdit();
    },
  });

  const isLoading = savingFull || savingPartial || loading || !data;

  return isLoading ? (
    <LoadingBackdrop open={isLoading} onClose={onClose} />
  ) : (
    <RecipeEditContent
      onClose={onClose}
      onEditPartial={input =>
        editRecipePartial({ variables: { input }, update: input.allergensEdit ? evictCachedRecipeAllergens : undefined })
      }
      onEditFull={input =>
        editRecipeFull({ variables: { input }, update: input.recipe.allergensEdit ? evictCachedRecipeAllergens : undefined })
      }
      recipe={data.recipe}
      onDuplicate={onDuplicate}
      forStaff={forStaff}
      readonly={readonly || isOrgGroupReadonly}
      onScaleAndCreateNewRecipe={onScaleAndCreateNewRecipe}
    />
  );
};
